export const RESIZE_MAX_EDGE = 3840;
export const RESIZE_MAX_ASPECT_RATIO = 3;
export const RESIZE_MIN_PIXELS = 655_360;
export const RESIZE_MAX_PIXELS = 8_294_400;

const parsePositiveNumber = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }

  return num;
};

const isAspectRatioValid = (width, height) => {
  const longEdge = Math.max(width, height);
  const shortEdge = Math.min(width, height);
  return longEdge / shortEdge <= RESIZE_MAX_ASPECT_RATIO;
};

const getAspectRatioSuggestionForPinnedField = (width, height, pinnedField) => {
  const fixedValue = pinnedField === "width" ? width : height;
  const otherField = pinnedField === "width" ? "height" : "width";
  const otherValue = pinnedField === "width" ? height : width;
  const isFixedLong = fixedValue >= otherValue;

  const suggestedValue = isFixedLong
    ? Math.max(1, Math.ceil(fixedValue / RESIZE_MAX_ASPECT_RATIO))
    : Math.max(1, Math.floor(fixedValue * RESIZE_MAX_ASPECT_RATIO));

  if (suggestedValue === otherValue) {
    return null;
  }

  const nextWidth = otherField === "width" ? suggestedValue : width;
  const nextHeight = otherField === "height" ? suggestedValue : height;

  if (!isAspectRatioValid(nextWidth, nextHeight)) {
    return null;
  }

  return { field: otherField, value: suggestedValue, keepField: pinnedField };
};

/**
 * @param {number} width
 * @param {number} height
 * @returns {Array<{ field: "width" | "height", value: number, keepField: "width" | "height" }>}
 */
export const getAspectRatioSuggestions = (width, height) => {
  const suggestions = [];

  const adjustWidthSuggestion = getAspectRatioSuggestionForPinnedField(
    width,
    height,
    "height"
  );
  const adjustHeightSuggestion = getAspectRatioSuggestionForPinnedField(
    width,
    height,
    "width"
  );

  if (adjustWidthSuggestion) {
    suggestions.push(adjustWidthSuggestion);
  }

  if (adjustHeightSuggestion) {
    suggestions.push(adjustHeightSuggestion);
  }

  return suggestions;
};

/**
 * @param {string | number} value
 * @returns {{ valid: true, value: number } | { valid: false, reason: "invalid" | "empty" }}
 */
export const validateResizeDimension = (value) => {
  const num = parsePositiveNumber(value);

  if (num === null) {
    if (value === "" || value === null || value === undefined) {
      return { valid: false, reason: "empty" };
    }

    return { valid: false, reason: "invalid" };
  }

  return { valid: true, value: num };
};

/**
 * @param {string | number} width
 * @param {string | number} height
 * @returns {{ valid: true, width: number, height: number, totalPixels: number } | { valid: false, reason: "aspectRatioExceeded", suggestions?: Array<{ field: "width" | "height", value: number, keepField: "width" | "height" }> } | null}
 */
export const validateResizeDimensionsGlobal = (width, height) => {
  const widthResult = validateResizeDimension(width);
  const heightResult = validateResizeDimension(height);

  if (
    !widthResult.valid ||
    !heightResult.valid ||
    widthResult.reason === "empty" ||
    heightResult.reason === "empty"
  ) {
    return null;
  }

  const normalizedWidth = widthResult.value;
  const normalizedHeight = heightResult.value;

  if (!isAspectRatioValid(normalizedWidth, normalizedHeight)) {
    return {
      valid: false,
      reason: "aspectRatioExceeded",
      suggestions: getAspectRatioSuggestions(normalizedWidth, normalizedHeight),
    };
  }

  return {
    valid: true,
    width: normalizedWidth,
    height: normalizedHeight,
    totalPixels: normalizedWidth * normalizedHeight,
  };
};

export const areResizeDimensionsReady = (width, height) =>
  validateResizeDimensionsGlobal(width, height)?.valid === true;
